/**
 * Native Biometric Authentication Service
 * 
 * Provides Face ID, Touch ID, and device passcode authentication
 * using Capacitor's native biometric plugin.
 * 
 * Features:
 * - Face ID (iOS) / Face Unlock (Android)
 * - Touch ID (iOS) / Fingerprint (Android)
 * - Device Passcode/PIN fallback
 * - Secure credential storage in Keychain/Keystore
 * - Web fallback using WebAuthn
 */

import { Capacitor } from '@capacitor/core';

// Dynamic import for native biometric plugin
let NativeBiometric = null;

// Biometry types
export const BIOMETRY_TYPE = {
  NONE: 'none',
  TOUCH_ID: 'touchId',
  FACE_ID: 'faceId',
  FINGERPRINT: 'fingerprint',
  FACE_AUTHENTICATION: 'faceAuthentication',
  IRIS_AUTHENTICATION: 'irisAuthentication',
  MULTIPLE: 'multiple',
};

// Storage key for biometric login
const BIOMETRIC_LOGIN_ENABLED = 'biometric_login_enabled';
const BIOMETRIC_USER_ID = 'biometric_user_id';
const BIOMETRIC_CREDENTIALS_SERVER = 'forgetmenot_credentials';

/**
 * Initialize the biometric plugin
 */
const initBiometricPlugin = async () => {
  if (NativeBiometric) return true;
  
  if (!Capacitor.isNativePlatform()) {
    console.log('[Biometric] Web platform - using WebAuthn fallback');
    return false;
  }
  
  try {
    const module = await import('@capgo/capacitor-native-biometric');
    NativeBiometric = module.NativeBiometric;
    return true;
  } catch (error) {
    console.error('[Biometric] Failed to load plugin:', error);
    return false;
  }
};

/**
 * Check if native biometric authentication is available
 */
export const isBiometricAvailable = async () => {
  const isNative = await initBiometricPlugin();
  
  if (isNative && NativeBiometric) {
    try {
      const result = await NativeBiometric.isAvailable();
      return {
        available: result.isAvailable,
        biometryType: result.biometryType || BIOMETRY_TYPE.NONE,
        errorCode: result.errorCode,
        isNative: true,
      };
    } catch (error) {
      console.error('[Biometric] Availability check failed:', error);
      return { available: false, biometryType: BIOMETRY_TYPE.NONE, isNative: true };
    }
  }
  
  // Web fallback - check WebAuthn support
  if (typeof window !== 'undefined' && window.PublicKeyCredential) {
    try {
      const available = await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
      return {
        available,
        biometryType: available ? BIOMETRY_TYPE.FINGERPRINT : BIOMETRY_TYPE.NONE,
        isNative: false,
      };
    } catch {
      return { available: false, biometryType: BIOMETRY_TYPE.NONE, isNative: false };
    }
  }
  
  return { available: false, biometryType: BIOMETRY_TYPE.NONE, isNative: false };
};

/**
 * Get user-friendly label for biometry type
 */
export const getBiometryLabel = (biometryType) => {
  switch (biometryType) {
    case BIOMETRY_TYPE.FACE_ID:
    case BIOMETRY_TYPE.FACE_AUTHENTICATION:
      return 'Face ID';
    case BIOMETRY_TYPE.TOUCH_ID:
      return 'Touch ID';
    case BIOMETRY_TYPE.FINGERPRINT:
      return 'Fingerprint';
    case BIOMETRY_TYPE.IRIS_AUTHENTICATION:
      return 'Iris';
    case BIOMETRY_TYPE.MULTIPLE:
      return 'Biometrics';
    default:
      return 'Passcode';
  }
};

/**
 * Verify user identity using biometrics
 * This prompts the user for Face ID/Touch ID/Fingerprint/Passcode
 */
export const verifyIdentity = async (options = {}) => {
  const {
    reason = 'Verify your identity to continue',
    title = 'Authentication Required',
    subtitle = '',
    description = '',
    fallbackTitle = 'Use Passcode',
    maxAttempts = 3,
    useFallback = true, // Allow device passcode as fallback
  } = options;
  
  const isNative = await initBiometricPlugin();
  
  if (isNative && NativeBiometric) {
    try {
      await NativeBiometric.verifyIdentity({
        reason,
        title,
        subtitle,
        description,
        fallbackTitle,
        maxAttempts,
        useFallback,
      });
      
      return { success: true };
    } catch (error) {
      console.error('[Biometric] Verification failed:', error);
      return {
        success: false,
        error: error.message || 'Authentication failed',
        cancelled: error.code === 'BIOMETRIC_DISMISSED' || error.code === 'BIOMETRIC_AUTHENTICATION_FAILED',
      };
    }
  }
  
  // Web fallback using WebAuthn
  return verifyIdentityWebAuthn(reason);
};

/**
 * WebAuthn fallback for web browsers
 */
const verifyIdentityWebAuthn = async (reason) => {
  if (!window.PublicKeyCredential) {
    return { success: false, error: 'Biometric authentication not supported' };
  }
  
  try {
    const challenge = new Uint8Array(32);
    crypto.getRandomValues(challenge);
    
    const credential = await navigator.credentials.get({
      publicKey: {
        challenge,
        timeout: 60000,
        userVerification: 'required',
        rpId: window.location.hostname,
      },
    });
    
    return { success: !!credential };
  } catch (error) {
    return {
      success: false,
      error: error.message || 'Authentication failed',
      cancelled: error.name === 'NotAllowedError',
    };
  }
};

/**
 * Store credentials securely for biometric login
 * Stores in native Keychain (iOS) or Keystore (Android)
 */
export const setCredentials = async (userId, email, sessionToken) => {
  const isNative = await initBiometricPlugin();
  
  if (isNative && NativeBiometric) {
    try {
      // Store credentials in secure enclave
      await NativeBiometric.setCredentials({
        server: BIOMETRIC_CREDENTIALS_SERVER,
        username: email,
        password: sessionToken,
      });
      
      // Mark biometric login as enabled
      localStorage.setItem(BIOMETRIC_LOGIN_ENABLED, 'true');
      localStorage.setItem(BIOMETRIC_USER_ID, userId);
      
      return { success: true };
    } catch (error) {
      console.error('[Biometric] Failed to store credentials:', error);
      return { success: false, error: error.message };
    }
  }
  
  // Web fallback - store encrypted in localStorage (less secure)
  try {
    const encoded = btoa(JSON.stringify({ email, token: sessionToken }));
    localStorage.setItem('biometric_credentials', encoded);
    localStorage.setItem(BIOMETRIC_LOGIN_ENABLED, 'true');
    localStorage.setItem(BIOMETRIC_USER_ID, userId);
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/**
 * Get stored credentials after biometric verification
 */
export const getCredentials = async () => {
  const isNative = await initBiometricPlugin();
  
  if (isNative && NativeBiometric) {
    try {
      const credentials = await NativeBiometric.getCredentials({
        server: BIOMETRIC_CREDENTIALS_SERVER,
      });
      
      return {
        success: true,
        email: credentials.username,
        sessionToken: credentials.password,
      };
    } catch (error) {
      console.error('[Biometric] Failed to get credentials:', error);
      return { success: false, error: error.message };
    }
  }
  
  // Web fallback
  try {
    const encoded = localStorage.getItem('biometric_credentials');
    if (!encoded) {
      return { success: false, error: 'No credentials stored' };
    }
    
    const { email, token } = JSON.parse(atob(encoded));
    return { success: true, email, sessionToken: token };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/**
 * Delete stored credentials
 */
export const deleteCredentials = async () => {
  const isNative = await initBiometricPlugin();
  
  if (isNative && NativeBiometric) {
    try {
      await NativeBiometric.deleteCredentials({
        server: BIOMETRIC_CREDENTIALS_SERVER,
      });
    } catch (error) {
      console.warn('[Biometric] Failed to delete native credentials:', error);
    }
  }
  
  // Clear local storage
  localStorage.removeItem('biometric_credentials');
  localStorage.removeItem(BIOMETRIC_LOGIN_ENABLED);
  localStorage.removeItem(BIOMETRIC_USER_ID);
  
  return { success: true };
};

/**
 * Check if biometric login is enabled for current device
 */
export const isBiometricLoginEnabled = () => {
  return localStorage.getItem(BIOMETRIC_LOGIN_ENABLED) === 'true';
};

/**
 * Get the user ID associated with biometric login
 */
export const getBiometricUserId = () => {
  return localStorage.getItem(BIOMETRIC_USER_ID);
};

/**
 * Perform biometric login
 * Verifies identity and returns stored credentials
 */
export const biometricLogin = async () => {
  // First verify the user's identity
  const verifyResult = await verifyIdentity({
    reason: 'Sign in to Forget Me Not',
    title: 'Quick Login',
    subtitle: 'Use Face ID or passcode to sign in',
    fallbackTitle: 'Use Passcode',
  });
  
  if (!verifyResult.success) {
    return {
      success: false,
      error: verifyResult.error || 'Authentication failed',
      cancelled: verifyResult.cancelled,
    };
  }
  
  // Get stored credentials
  const credentials = await getCredentials();
  
  if (!credentials.success) {
    return {
      success: false,
      error: 'No saved login found. Please sign in with email and password first.',
    };
  }
  
  return {
    success: true,
    email: credentials.email,
    sessionToken: credentials.sessionToken,
  };
};

/**
 * Enable biometric login after successful password login
 */
export const enableBiometricLogin = async (userId, email, sessionToken) => {
  // First verify the user wants to enable it
  const availability = await isBiometricAvailable();
  
  if (!availability.available) {
    return {
      success: false,
      error: 'Biometric authentication is not available on this device',
    };
  }
  
  // Store credentials
  const result = await setCredentials(userId, email, sessionToken);
  
  if (result.success) {
    console.log('[Biometric] Login enabled for user:', userId);
  }
  
  return result;
};

/**
 * Disable biometric login
 */
export const disableBiometricLogin = async () => {
  await deleteCredentials();
  console.log('[Biometric] Login disabled');
  return { success: true };
};

export default {
  isBiometricAvailable,
  getBiometryLabel,
  verifyIdentity,
  setCredentials,
  getCredentials,
  deleteCredentials,
  isBiometricLoginEnabled,
  getBiometricUserId,
  biometricLogin,
  enableBiometricLogin,
  disableBiometricLogin,
  BIOMETRY_TYPE,
};
